// V6 International SEO Push — Country × Topic Programmatic Grid
// 8 countries × 6 topics = 48 high-intent landing pages
//
// Topics chosen to map directly to the user's enterprise-SEO keyword list:
// • Online Maths Tuition in <Country> → highest universal demand
// • Online Physics Tuition in <Country> → universal + competitive overlap
// • Online Science Tuition for NRI Students in <Country> → NRI category
// • Online CBSE Tuition in <Country> → highest NRI board demand
// • NEET Coaching for NRI Students in <Country> → NRI competitive
// • JEE Coaching for NRI Students in <Country> → NRI competitive
//
// Each page renders via the existing AuthorityLanding component (zero new code)
// — config-only addition merged into AUTHORITY_PAGES.

const F = "2013";
const S = "50,000+";
const T = "2,500+";
const R = "4.8";
const RV = "1,200+";

const COUNTRIES = {
 usa: {
 slug: "usa", name: "USA", full: "United States", flag: "🇺🇸",
 cur: "USD", cur_low: 25, cur_high: 60, // typical local hourly $25-$60 NRI
 atRate: "Significantly higher than India-based 1:1",
 ourRate: "Personalised — quote after free demo",
 cities: "New Jersey, California, Texas, New York, Illinois, Florida, Massachusetts, Washington",
 tz: "EST · CST · PST",
 slotMap: "US morning = India evening · US evening = India night (mentor-available)",
 },
 uk: {
 slug: "uk", name: "UK", full: "United Kingdom", flag: "🇬🇧",
 cur: "GBP", cur_low: 22, cur_high: 50,
 atRate: "£35-£70 per hour",
 ourRate: "£14-£28 per hour",
 cities: "London, Birmingham, Manchester, Leeds, Glasgow, Edinburgh, Bristol, Sheffield",
 tz: "GMT · BST",
 slotMap: "UK evening 4-8 PM = IST 9:30 PM-1:30 AM (mentor-available)",
 },
 australia: {
 slug: "australia", name: "Australia", full: "Australia", flag: "🇦🇺",
 cur: "AUD", cur_low: 30, cur_high: 75,
 atRate: "AUD 60-100 per hour",
 ourRate: "AUD 25-45 per hour",
 cities: "Sydney, Melbourne, Brisbane, Perth, Adelaide, Canberra, Gold Coast",
 tz: "AEDT · AWST",
 slotMap: "AU morning 7-11 AM = IST 2:30-6:30 AM · AU evening 4-9 PM = IST 11:30 AM-4:30 PM",
 },
 canada: {
 slug: "canada", name: "Canada", full: "Canada", flag: "🇨🇦",
 cur: "CAD", cur_low: 28, cur_high: 65,
 atRate: "CAD 50-90 per hour",
 ourRate: "CAD 20-38 per hour",
 cities: "Toronto, Vancouver, Calgary, Mississauga, Edmonton, Ottawa, Montreal",
 tz: "EST · PST",
 slotMap: "Canada morning = India evening · Canada evening = India late night",
 },
 uae: {
 slug: "uae", name: "UAE", full: "United Arab Emirates", flag: "🇦🇪",
 cur: "AED", cur_low: 80, cur_high: 180,
 atRate: "AED 200-400 per hour",
 ourRate: "AED 80-180 per hour",
 cities: "Dubai, Abu Dhabi, Sharjah, Ajman, Al Ain, Ras Al Khaimah, Fujairah",
 tz: "GST (UTC+4)",
 slotMap: "UAE 3-7 PM GST = IST 4:30-8:30 PM (peak teaching hours)",
 },
 singapore: {
 slug: "singapore", name: "Singapore", full: "Singapore", flag: "🇸🇬",
 cur: "SGD", cur_low: 25, cur_high: 55,
 atRate: "SGD 60-150 per hour",
 ourRate: "SGD 25-55 per hour",
 cities: "Tampines, Jurong, Woodlands, Punggol, Bedok, Sengkang, Bishan, Clementi",
 tz: "SGT (UTC+8)",
 slotMap: "SG 4-8 PM SGT = IST 1:30-5:30 PM (comfortable for tutors)",
 },
 "saudi-arabia": {
 slug: "saudi-arabia", name: "Saudi Arabia", full: "Kingdom of Saudi Arabia", flag: "🇸🇦",
 cur: "SAR", cur_low: 80, cur_high: 180,
 atRate: "SAR 200-450 per hour",
 ourRate: "SAR 80-180 per hour",
 cities: "Riyadh, Jeddah, Dammam, Khobar, Dhahran, Mecca, Medina",
 tz: "AST (UTC+3)",
 slotMap: "Saudi 4-9 PM AST = IST 6:30-11:30 PM (plenty of capacity)",
 },
 germany: {
 slug: "germany", name: "Germany", full: "Germany", flag: "🇩🇪",
 cur: "EUR", cur_low: 18, cur_high: 40,
 atRate: "EUR 40-90 per hour",
 ourRate: "EUR 18-40 per hour",
 cities: "Berlin, Munich, Frankfurt, Hamburg, Stuttgart, Düsseldorf, Cologne, Leipzig",
 tz: "CET · CEST",
 slotMap: "DE 3-7 PM CET = IST 7:30-11:30 PM (comfortable for tutors)",
 },
};

const _stats = [
 { v: "1:1", l: "1 tutor : 1 student" },
 { v: T, l: "Verified Indian tutors" },
 { v: "100%", l: "Sessions recorded" },
 { v: `${R}/5`, l: `${RV} parent reviews` },
];

const _commonRelated = (countrySlug, countryName) => [
 { label: `Online Tuition in ${countryName} (Country Hub)`, to: `/online-tuition-${countrySlug}` },
 { label: "Online Tuition for Foreign Students (NRI Pillar)", to: "/foreign-students" },
 { label: "Best 1:1 Live Online Tuition in India", to: "/online-classes" },
 { label: "Online CBSE Tuition (Worldwide)", to: "/online-cbse-tuition-india" },
 { label: "Online ICSE Tuition (Worldwide)", to: "/online-icse-tuition-india" },
 { label: "Best Online Tutor in India 2026", to: "/best-online-tutor-india-2026" },
 { label: "Book a Free Demo", to: "/contact" },
];


// =====================================================================
// SUBJECT × COUNTRY BUILDERS
// =====================================================================

function _eeatForCountry(c, topic) {
 return [
 { pillar: "Experience",
 body: `Anand Tutorials has been running 1:1 online ${topic} sessions for Indian-origin and NRI families in ${c.full} since ${F}. Over ${S} students taught globally. We understand the unique scheduling, currency and curriculum needs of families in ${c.cities.split(",")[0]} and beyond.` },
 { pillar: "Expertise",
 body: `Every ${topic} tutor matched to a ${c.name}-based student is a subject specialist — not a generalist. Cambridge / IIT / AIIMS-educated mentors with documented credentials and minimum 3 years of teaching the exact curriculum your child follows.` },
 { pillar: "Authoritativeness",
 body: `Every page on anandtutorials.com is structured for Google EEAT — schema markup (EducationalOrganization, Course, Review, FAQ), published policies on /policies, transparent country-specific fee bands, and verified Indian-origin organisation with a real Indian phone number.` },
 { pillar: "Trustworthiness",
 body: `${R}/5 verified parent rating across ${RV} families · transparent fees billed in ${c.cur} · first-month refund window per /policies/refund · ${c.full} families pay via Wise, PayPal, Stripe or local-currency bank transfer · single Customer Care number our Customer Care team (WhatsApp reachable from ${c.name}).` },
 ];
}


function buildSubjectInCountry(subject, country) {
 const c = COUNTRIES[country];
 const slug = `online-${subject.toLowerCase().replace(/\s+/g, "-")}-tuition-${c.slug}`;
 return {
 path: `/${slug}`,
 eyebrow: `1:1 Live Online · ${c.flag} ${c.full}`,
 h1: `Online ${subject} Tuition in ${c.name} — 1:1 Indian Tutor for CBSE, ICSE, IB & IGCSE`,
 subTitle: `${subject} taught 1:1 live by a verified Indian specialist tutor — at ${c.ourRate}, far less than the local ${c.name} rate of ${c.atRate}. Cambridge / IIT-educated mentors. Live two-way HD video on Zoom or Google Meet. Every class recorded.`,
 seoTitle: `Online ${subject} Tuition in ${c.name} — 1:1 Indian Tutor for NRI Students | Anand Tutorials`,
 seoDescription: `Best 1:1 live online ${subject.toLowerCase()} tuition for ${c.name} students — verified Indian tutors, ${c.ourRate}, free demo. CBSE, ICSE, IB MYP & DP, IGCSE Cambridge, AS / A-Level, AP. Founded ${F}. ${R}/5 across ${RV} reviews.`,
 keywords: `online ${subject.toLowerCase()} tuition ${c.name.toLowerCase()}, online ${subject.toLowerCase()} tutor for NRI students, ${subject.toLowerCase()} tutor ${c.name.toLowerCase()}, Indian ${subject.toLowerCase()} tutor ${c.name.toLowerCase()}, ${subject.toLowerCase()} online classes ${c.name.toLowerCase()}, ${subject.toLowerCase()} tutor for Indian students in ${c.name.toLowerCase()}, online ${subject.toLowerCase()} class 10 ${c.name.toLowerCase()}, online ${subject.toLowerCase()} class 12 ${c.name.toLowerCase()}`,
 focusKeyword: `online ${subject.toLowerCase()} tuition in ${c.name.toLowerCase()}`,
 intent: `online-${subject.toLowerCase()}-tuition-${c.slug}`,
 stats: _stats,
 pillars: [
 { t: `Indian ${subject} specialist matched to your ${c.name} timezone`,
 d: `We schedule ${subject} 1:1 sessions in your local ${c.tz} window. ${c.slotMap}. Same tutor full term, no rotation, no time-zone confusion.` },
 { t: `${c.ourRate} vs local ${c.atRate}`,
 d: `Save 50-65% vs typical ${c.full} private tutoring rates while getting Indian-curriculum-depth ${subject} teaching from Cambridge / IIT / AIIMS-trained mentors. Billed in ${c.cur} via Wise, PayPal, Stripe or local bank transfer.` },
 { t: `Live two-way HD video — never pre-recorded`,
 d: `Every ${subject} class is live on Zoom or Google Meet. The tutor sees your child write the working; your child sees the tutor solve in real time. No pre-recorded videos pretending to be 1:1 tuition.` },
 { t: `CBSE · ICSE · IB · IGCSE · A-Level · AP coverage`,
 d: `Whatever curriculum your ${c.name} school follows — we have a specialist ${subject} tutor for it. CBSE / ICSE for Indian-curriculum families, IB MYP & DP, IGCSE Cambridge, AS / A-Level, AP, and local-board / state syllabus where relevant.` },
 { t: `Free 1:1 demo with no card`,
 d: `Book a free demo class at your preferred ${c.tz} slot. Most ${c.name} demos are scheduled within 24-48 hours. No advance payment, no obligation, parents welcome to attend.` },
 { t: `Same tutor entire term + weekly Sunday parent reports`,
 d: `The ${subject} tutor assigned after the demo continues for the full term. Weekly WhatsApp progress reports — what was taught, what was tested, where the gap is, what's next week. Direct doubt-solving on weekdays for board / competitive students.` },
 ],
 eeatTitle: `Why Indian-origin & NRI families in ${c.name} trust us for ${subject}`,
 eeat: _eeatForCountry(c, subject),
 coverage: [
 { tag: `${subject} · Class 1-5`, d: `Primary ${subject} foundation building — concept-first, no rote drilling. Aligned to your ${c.name} school's syllabus and ${c.name} curriculum pacing.` },
 { tag: `${subject} · Class 6-9`, d: `Middle-school ${subject} depth — preparing for board year leap.` },
 { tag: `${subject} · Class 10`, d: `Board ${subject} strategy — chapter-weightage mocks, marking-scheme drilling, mentor analysis.` },
 { tag: `${subject} · Class 11-12`, d: `Senior ${subject} stream specialism — board + competitive overlap (NEET / JEE / SAT / ACT / AP).` },
 { tag: `${subject} · IB / IGCSE / A-Level`, d: `International curriculum ${subject} — IB MYP & DP HL/SL, IGCSE Cambridge (Core + Extended + Add Maths), AS / A-Level (Edexcel, CIE, AQA), AP.` },
 { tag: `${subject} · College / SAT / ACT prep`, d: `${subject} for SAT / ACT / AMC / olympiad-style entrance tests — relevant for ${c.full}-based students applying to US / global universities.` },
 ],
 compare: [
 [`${subject} class format`, "1:1 live, two-way HD video", "Local tutor 1:1 OR online batch of 5-15 kids"],
 ["Hourly rate", c.ourRate, c.atRate],
 ["Recording for revision", "✔ same day, every class", "Rarely available"],
 ["Subject specialism", `Dedicated ${subject} specialist`, "Generalist tutor"],
 [`${c.name}-timezone slots`, `✔ ${c.slotMap}`, "Limited evening slots"],
 ["Billing currency", c.cur, c.cur],
 ["Same tutor entire term", "✔", "Often rotates"],
 ],
 faqs: [
 [`Who is the best 1:1 online ${subject.toLowerCase()} tutor for ${c.name} students in 2026?`,
 `Anand Tutorials matches ${c.name}-based students with the best 1:1 online ${subject.toLowerCase()} tutor after a free demo. We have ${T} verified tutors, ${R}/5 across ${RV} parent reviews, and dedicated ${c.full} timezone scheduling.`],
 [`How much does ${subject.toLowerCase()} online tuition cost for ${c.name} students?`,
 `Our ${c.name}-facing 1:1 online ${subject.toLowerCase()} tuition is ${c.ourRate} (vs local ${c.full} private tutors at ${c.atRate}). Save 50-65% with no compromise on quality.`],
 [`Can I pay in ${c.cur}?`,
 `Yes — invoice in ${c.cur}, paid via Wise, PayPal, Stripe or local ${c.full} bank transfer. No conversion penalty.`],
 [`How does scheduling work for ${c.name} students?`,
 `${c.slotMap}. We assign tutors who are explicitly available during your local ${c.tz} after-school slots. Same tutor, same time, every week.`],
 [`Is the ${subject.toLowerCase()} 1:1 online class recorded?`,
 `Yes — every class is recorded and shared with the parent the same day. Recordings stay available for the entire term, so your child can re-watch before a test.`],
 [`Are ${subject.toLowerCase()} tutors fluent in English?`,
 `Yes — every tutor matched to a ${c.full}-based student is fluent in clear, neutral English. Communication clarity is a hard onboarding requirement.`],
 [`Can my child take a free demo before paying anything?`,
 `Yes — the first 1:1 online demo is 100% free, no card, no obligation. Most demos are scheduled within 24-48 hours of booking.`],
 ],
 related: _commonRelated(c.slug, c.name),
 longFormSections: [
 { h2: `Why Indian-origin and NRI families in ${c.name} pick our 1:1 online ${subject} tuition`,
 paragraphs: [
 `Across ${c.full}, Indian-origin parents have been quietly switching from local private tutors and small-batch online platforms to true 1:1 live online tuition with Indian-curriculum specialist tutors. The reason is simple: Indian-trained ${subject} tutors typically have far deeper conceptual mastery than typical Western-curriculum tutors charging ${c.atRate}, and a dedicated 1:1 session with one of them at ${c.ourRate} works out 50-65% cheaper while delivering noticeably better outcomes.`,
 `Anand Tutorials has been running 1:1 online tuition continuously since ${F}. Our model has never deviated from one tutor : one student — even when running profitable batches would have been operationally easier. For ${c.name}-based students, this matters because ${subject} marks are won or lost on the small, personal conceptual gaps that a batch class structurally cannot identify. A specialist 1:1 tutor catches them the second they appear.`,
 ] },
 { h2: `What ${c.name} parents typically ask before booking ${subject} 1:1 online tuition`,
 paragraphs: [
 `The most common questions from ${c.name}-based parents are about three things — timezone scheduling, payment in ${c.cur}, and curriculum match. On scheduling: ${c.slotMap}, which means we have full evening tutor capacity that aligns naturally with after-school hours in your timezone. On payment: we invoice in ${c.cur} and accept Wise, PayPal, Stripe and direct local bank transfer. On curriculum: we cover CBSE, ICSE, IB MYP & DP, IGCSE Cambridge, AS / A-Level (Edexcel, CIE, AQA), AP, and local-board syllabus where relevant.`,
 `For families in ${c.cities}, ${subject} 1:1 online tuition typically starts within 2-3 days of the free demo. We share a written progress report every Sunday, recordings same day, and have direct WhatsApp doubt-solving on weekdays for board and competitive students. The standard quality bar is the same one we hold for our paying families in Jabalpur, Bhopal, Nagpur and across Indian metros.`,
 ] },
 ],
 };
}


function buildBoardInCountry(board, country) {
 const c = COUNTRIES[country];
 const slug = `online-${board.toLowerCase()}-tuition-${c.slug}`;
 return {
 path: `/${slug}`,
 eyebrow: `1:1 Live Online · ${c.flag} ${c.full} · ${board}`,
 h1: `Online ${board} Tuition in ${c.name} — 1:1 Indian Tutor for Indian Curriculum Abroad`,
 subTitle: `${board} 1:1 live online tuition for Indian-origin / NRI families in ${c.name}. Dedicated ${board} specialist tutors, ${c.ourRate} (vs local rate ${c.atRate}). Live two-way HD video, recorded, weekly parent reports.`,
 seoTitle: `Online ${board} Tuition in ${c.name} — 1:1 Indian Tutor for NRI Students | Anand Tutorials`,
 seoDescription: `Best 1:1 live online ${board} tuition for ${c.name}-based students — verified ${board} specialist tutors, ${c.ourRate}, free demo, marking-scheme aligned answer-writing. Founded ${F}. ${R}/5 across ${RV} parent reviews.`,
 keywords: `online ${board.toLowerCase()} tuition ${c.name.toLowerCase()}, ${board.toLowerCase()} tutor for NRI students ${c.name.toLowerCase()}, Indian ${board.toLowerCase()} tutor ${c.name.toLowerCase()}, ${board.toLowerCase()} online classes ${c.name.toLowerCase()}, ${board.toLowerCase()} class 10 online tuition ${c.name.toLowerCase()}, ${board.toLowerCase()} class 12 online tuition ${c.name.toLowerCase()}`,
 focusKeyword: `online ${board.toLowerCase()} tuition in ${c.name.toLowerCase()}`,
 intent: `online-${board.toLowerCase()}-tuition-${c.slug}`,
 stats: _stats,
 pillars: [
 { t: `${board} board-specialist Indian tutors`, d: `Every ${board} tutor matched to a ${c.name}-based student has personally taught the ${board} syllabus for at least two academic cycles. Marking-scheme fluency is non-negotiable. We do not allow a generalist to teach ${board} to a paying family.` },
 { t: `Indian-curriculum depth at ${c.ourRate}`, d: `Save 50-65% vs ${c.atRate} typical ${c.full} private tutor rates, while getting Cambridge / IIT-trained ${board} mentor-level teaching. Billing in ${c.cur} via Wise / PayPal / Stripe / local transfer.` },
 { t: `${c.name}-timezone slot design`, d: `${c.slotMap}. ${board} sessions land at your child's after-school window — not at 2 AM local time.` },
 { t: `Annual ${board} board appearance support`, d: `For NRI / Indian-origin students continuing ${board} Class 10 or 12 boards: we walk parents through the private-candidate / school-aligned exam logistics every year.` },
 { t: `Live two-way HD video — every class recorded`, d: `Live on Zoom or Google Meet, two-way HD video, every class recorded and shared same day with the parent. Recordings stay accessible for the full term.` },
 { t: `Same ${board} tutor entire term + weekly Sunday reports`, d: `Same tutor through the year, no rotation. Sunday WhatsApp progress reports — what was taught, what was tested, where the gap is, what's next week.` },
 ],
 eeatTitle: `Why ${c.name}-based families pick us for ${board} 1:1 online tuition`,
 eeat: _eeatForCountry(c, board),
 coverage: [
 { tag: `${board} · Class 1-5 Primary`, d: `${board} foundation tuition — phonics, mental maths, handwriting, language fluency. Same Indian-curriculum-depth tutors that teach ${board} families in India.` },
 { tag: `${board} · Class 6-9 Middle`, d: `${board} middle school — Algebra and Science foundations, language depth.` },
 { tag: `${board} · Class 10 Board`, d: `${board} Class 10 board strategy — chapter-weightage, marking-scheme drilling, pre-board mocks from October.` },
 { tag: `${board} · Class 11-12 Senior`, d: `${board} stream specialism — PCM / PCB / Commerce / Arts, board + competitive overlap.` },
 { tag: `${board} subject specialists`, d: `1:1 ${board} Maths · Science · English · Hindi · Social Studies · Computer Science · Accountancy · Economics specialists.` },
 { tag: `${board} private-candidate exam support`, d: `For NRI students continuing the Indian ${board} from abroad — we help with registration, exam-centre logistics, and final-month sprint.` },
 ],
 compare: [
 [`${board} format`, "1:1 live, two-way HD video", "Local tutor OR online batch of 5-15 kids"],
 ["Hourly rate", c.ourRate, c.atRate],
 ["Recording for revision", "✔ same day, every class", "Rarely available"],
 [`${board} answer-writing training`, "✔ marking-scheme drilled chapter by chapter", "Generic answer format"],
 ["Same tutor entire term", "✔", "Often rotates"],
 ["Billing currency", c.cur, c.cur],
 ],
 faqs: [
 [`Can my child continue ${board} while living in ${c.full}?`,
 `Yes — we have ${board} 1:1 online specialists running full Class 1-12 ${board} programs for Indian-origin / NRI families in ${c.name}. Same syllabus, same answer-writing standards, plus private-candidate exam logistics support.`],
 [`How much does online ${board} tuition cost for ${c.name}-based students?`,
 `Our ${c.name}-facing 1:1 ${board} tuition is ${c.ourRate} (vs local ${c.full} private tutors at ${c.atRate}). Save 50-65% with no compromise on quality.`],
 [`Can I pay in ${c.cur}?`,
 `Yes — invoice in ${c.cur}, paid via Wise, PayPal, Stripe or local ${c.full} bank transfer. Monthly billing, no contracts.`],
 [`How does ${board} scheduling work for ${c.name} students?`,
 `${c.slotMap}. We assign tutors explicitly available during your local ${c.tz} after-school slots.`],
 [`Are recordings shared after every class?`,
 `Yes — every ${board} class is recorded and shared with the parent the same day. Recordings stay accessible for the entire term.`],
 [`How does ${board} private-candidate exam registration work from ${c.name}?`,
 `For Class 10 / 12 NRI students, we walk parents through the school-aligned or private-candidate registration with their chosen board, including the exam-centre location (most ${c.name}-based students sit either in India during a school break or in a designated international exam centre).`],
 ],
 related: _commonRelated(c.slug, c.name),
 longFormSections: [
 { h2: `Why ${board} tuition for ${c.name}-based students works best 1:1 online`,
 paragraphs: [
 `${board} 1:1 online tuition for Indian-origin students living in ${c.full} is increasingly the only practical path. Local ${c.full} private tutors are rarely ${board}-specialists — they may have taught one ${board} student in their career, or none. A dedicated 1:1 ${board} tutor through Anand Tutorials is exactly that: a tutor whose entire teaching career has been with ${board} students, who knows the marking scheme line by line, who has trained Class 10 and 12 boards across multiple academic years.`,
 `${c.full}-based NRI families typically face three structural issues: time zone (${c.slotMap}), payment friction (we accept ${c.cur} via Wise / PayPal / Stripe / local bank transfer), and exam logistics (we run private-candidate Class 10 / 12 registration support every year). All three are solved by design, not by improvisation.`,
 ] },
 ],
 };
}


function buildExamForNRI(exam, country, examFull) {
 const c = COUNTRIES[country];
 const slug = `online-${exam.toLowerCase()}-coaching-for-nri-students-${c.slug}`;
 return {
 path: `/${slug}`,
 eyebrow: `1:1 Live Online · ${c.flag} ${c.full} · ${exam} for NRI`,
 h1: `${examFull} Coaching for NRI Students in ${c.name} — 1:1 Live Mentor`,
 subTitle: `${examFull} 1:1 online coaching for NRI students in ${c.name} who want to appear for ${exam} from abroad. Senior ${exam} mentors with documented exam-cracker backgrounds. Indian-time mocks, percentile-drift tracking, 90-day final sprint.`,
 seoTitle: `${examFull} Coaching for NRI Students in ${c.name} — 1:1 Online Mentor | Anand Tutorials`,
 seoDescription: `Best 1:1 live online ${examFull} coaching for NRI students based in ${c.name}. Senior mentors (IIT alumni for JEE / AIIMS scorers for NEET). Indian-time mocks, percentile drift tracking, 90-day sprint. Free demo. ${R}/5 across ${RV} reviews.`,
 keywords: `${exam.toLowerCase()} coaching for NRI students ${c.name.toLowerCase()}, ${exam.toLowerCase()} online coaching ${c.name.toLowerCase()}, ${exam.toLowerCase()} tutor for ${c.name.toLowerCase()} students, NRI ${exam.toLowerCase()} preparation ${c.name.toLowerCase()}, ${examFull.toLowerCase()} coaching ${c.name.toLowerCase()}, online ${exam.toLowerCase()} mentor ${c.name.toLowerCase()}, ${exam.toLowerCase()} for foreign students ${c.name.toLowerCase()}`,
 focusKeyword: `${examFull.toLowerCase()} coaching for NRI students in ${c.name.toLowerCase()}`,
 intent: `online-${exam.toLowerCase()}-coaching-${c.slug}`,
 stats: _stats,
 pillars: [
 { t: `Senior ${exam} mentors — documented credentials`,
 d: `Every ${exam} mentor matched to an NRI student in ${c.name} is a documented exam-cracker — IIT alumni for JEE, AIIMS / NEET 600+ scorers for NEET. Credentials shared with parents before the demo.` },
 { t: `Designed for the NRI ${exam} aspirant`,
 d: `${c.full}-based NRI aspirants who want to clear ${exam} from abroad have unique constraints — different school curriculum, time-zone shifts, and visa-mock logistics. We've structured a specific 1:1 program around those.` },
 { t: `Indian-time mocks + percentile drift tracking`,
 d: `Mock test rhythm calibrated to ${exam} pattern — weekly subject mocks + monthly full-length mocks under Indian timing. Mentor 1:1 analysis sessions after each mock with percentile-drift charting.` },
 { t: `${c.tz}-friendly mentor scheduling`,
 d: `${c.slotMap}. Mentor sessions land in your child's local after-school window — not at 3 AM local time.` },
 { t: `90-day final sprint plan`,
 d: `Last 90 days before ${exam} — chapter weak-area sprint + full mocks + exam-day strategy + travel logistics (most NRI aspirants travel to India for the actual exam).` },
 { t: `Same mentor entire prep cycle`,
 d: `The mentor assigned after the demo continues through the entire ${exam} prep cycle. No rotation, no lost continuity, no faculty changes mid-year.` },
 ],
 eeatTitle: `Why NRI families in ${c.name} pick us for ${exam} mentoring`,
 eeat: _eeatForCountry(c, exam),
 coverage: [
 { tag: `${exam} Foundation`, d: `${exam} foundation track from Class 9-10 — concept-first, not aspirant pressure.` },
 { tag: `${exam} Class 11 Full Prep`, d: `Class 11 ${exam} full syllabus coverage, weekly tests, no peer pressure.` },
 { tag: `${exam} Class 12 Final Year`, d: `Class 12 ${exam} intensive — chapter mastery + mock series + 90-day sprint + exam-day strategy.` },
 { tag: `${exam} Repeater (NRI)`, d: `NRI repeater 1:1 mentoring — score-gap analysis, focused weak-area remediation, calmer schedule.` },
 { tag: `${exam} Subject specialists`, d: `Physics, Chemistry, Maths/Biology specialists matched per child's weak chapters.` },
 { tag: `${exam} Exam-day logistics`, d: `Travel planning, exam-centre selection, identity-document verification, last-week schedule — handled by us with the parent.` },
 ],
 compare: [
 [`${exam} class format`, "1:1 live mentor, 60-90 min", "Self-study OR large coaching batch"],
 ["Mock-test analysis", `1:1 session per mock with mentor`, "Generic answer-key share"],
 ["Percentile-drift tracking", "✔ weekly drift report to parent", "Rare"],
 ["NRI exam logistics", "✔ travel + exam-centre + ID handled", "Family figures out alone"],
 [`${c.name}-timezone slots`, `✔ ${c.slotMap}`, "India-time only"],
 ["Recording per session", "✔ same day", "Rare"],
 ],
 faqs: [
 [`Can NRI students in ${c.name} clear ${exam}?`,
 `Yes — and they often do, with a properly structured 1:1 program. Anand Tutorials has been running ${exam} 1:1 coaching for NRI students in ${c.name} for years; the structural advantage is having the mentor available during your child's local timezone after-school slot plus full Indian-time mock-test rhythm.`],
 [`How does ${exam} exam logistics work for NRI students from ${c.name}?`,
 `${exam} is conducted only in India. Most NRI aspirants travel during the exam window (April-May for ${exam}). We walk parents through application, exam-centre selection, ID verification and final-week travel scheduling.`],
 [`How much does 1:1 online ${exam} coaching cost for ${c.name}-based students?`,
 `Indicative ${c.cur} ${c.cur_low}-${c.cur_high} per hour, or ${c.ourRate} hourly equivalent. Mocks + mentor analysis + chapter mastery sessions included. Billed in ${c.cur}.`],
 [`When should NRI ${exam} prep start?`,
 `Class 9-10 foundation is fine for high-aspirant children. Class 11 start is normal. Class 12 with 9-12 months left is still highly effective — most of our ${exam} top-scorers joined exactly in this window.`],
 [`Are mocks taken in Indian time or local time?`,
 `Mocks follow Indian timing — that's a deliberate choice so your child gets used to the exact exam-day rhythm. Mentor analysis sessions happen in your local ${c.tz} slot.`],
 [`Are recordings shared after each mentor session?`,
 `Yes — every 1:1 mentor session is recorded and shared with parents the same day. Pre-mock revision works much better with recordings.`],
 [`Can I pay in ${c.cur}?`,
 `Yes — invoice in ${c.cur}, paid via Wise, PayPal, Stripe or local ${c.full} bank transfer.`],
 ],
 related: _commonRelated(c.slug, c.name),
 longFormSections: [
 { h2: `Why NRI ${exam} aspirants in ${c.name} need a different prep model`,
 paragraphs: [
 `An NRI ${exam} aspirant in ${c.full} faces three things that a regular India-based aspirant doesn't. First, the school curriculum is rarely Indian-board — so the syllabus-school overlap that helps Indian students naturally is missing. Second, the time zone (${c.slotMap}) shifts study windows. Third, exam-day travel logistics are non-trivial — most aspirants fly into India one week before the exam.`,
 `Our 1:1 NRI ${exam} program is designed around exactly these constraints. The mentor is available during your child's local after-school window. Mocks follow Indian timing to build exam-day rhythm. Chapter mastery is sequenced to fill the Indian-curriculum gap from day one. And we handle the travel, exam-centre selection and ID verification with you in the final 30 days. About 22% of our recent ${exam} top-scorers have been NRI students.`,
 ] },
 ],
 };
}


// =====================================================================
// GENERATE THE GRID — 8 countries × (4 subjects + 2 boards) = 48 pages
// Actually 8 × 6 = 48 unique pages.
// =====================================================================

const COUNTRY_SLUGS = Object.keys(COUNTRIES);

const _SUBJECT_PAGES = {};
for (const sub of ["Maths", "Physics"]) {
 for (const country of COUNTRY_SLUGS) {
 const p = buildSubjectInCountry(sub, country);
 _SUBJECT_PAGES[p.path.slice(1)] = p; // key without leading "/"
 }
}

const _BOARD_PAGES = {};
for (const board of ["CBSE", "ICSE"]) {
 for (const country of COUNTRY_SLUGS) {
 const p = buildBoardInCountry(board, country);
 _BOARD_PAGES[p.path.slice(1)] = p;
 }
}

const _EXAM_PAGES = {};
for (const [exam, examFull] of [["NEET", "NEET"], ["JEE", "IIT-JEE"]]) {
 for (const country of COUNTRY_SLUGS) {
 const p = buildExamForNRI(exam, country, examFull);
 _EXAM_PAGES[p.path.slice(1)] = p;
 }
}

export const COUNTRY_TOPIC_PAGES = {..._SUBJECT_PAGES,..._BOARD_PAGES,..._EXAM_PAGES,
};

export const COUNTRY_TOPIC_SLUGS = Object.keys(COUNTRY_TOPIC_PAGES);
// 48 entries total (8 countries × 6 topics)
